import { initializeYjsDocumentStageData } from '@hendrx/core/common';
import { ScoreWeightSummary } from '@hendrx/core/common/components/content/ScoreWeightSummary';
import { DndPreview } from '@hendrx/core/common/components/dnd/DndPreview';
import { Pages } from '@hendrx/core/common/components/pages/Pages';
import { RichTextToolbarTarget } from '@hendrx/core/common/components/richTexts/RichTextToolbarTarget/RichTextToolbarTarget';
import { SideMenu } from '@hendrx/core/common/components/studio/SideMenu';
import { CreateThemeContent } from '@hendrx/core/common/components/themes/Theme/CreateThemeContent/CreateThemeContent';
import { UpdateThemeContent } from '@hendrx/core/common/components/themes/Theme/UpdateThemeContent/UpdateThemeContent';
import { YjsSyncedWrapper } from '@hendrx/core/common/components/yjs/YjsSyncedWrapper/YjsSyncedWrapper';
import { stageContainerHeightVar, widgetManifests } from '@hendrx/core/common/contentObjects';
import { LearningActivityPageWrapper } from '@hendrx/core/learningActivity/components/LearningActivityPageWrapper';
import { LearningActivityStudioEditProviders } from '@hendrx/core/learningActivity/components/LearningActivityStudioEditProviders';
import { LearningActivityStudioTopBar } from '@hendrx/core/learningActivity/components/LearningActivityStudioTopBar';
import { LearningActivityToolbox } from '@hendrx/core/learningActivity/components/LearningActivityToolbox';
import { useGetLearningActivityDraftByIdQuery } from '@hendrx/core/learningActivity/queries/getLearningActivityDraftById';
import { Plugins } from '@hendrx/core/plugins/components/Plugins';
import { AppShell, Center, Container, rem } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CustomLoadingOverlay } from '@hendrx/shared/components/app/CustomLoadingOverlay';
import { HEADER_HEIGHT_PX } from 'shared/client/consts/styleConsts';
import { modals } from 'shared/client/utils/modals.utils';
import { useLearningObjectCommonData } from 'shared/components/providers/LearningObjectCommonDataProvider';
import { sidebarWidth } from 'shared/utils/theme.utils';
import { useUiLanguage } from 'shared/utils/useUiLanguage';

import { useStore } from '@/store';

export const Route = createFileRoute('/studio/learning-activity/edit/$learningActivityDraftId/')({
  validateSearch: z.object({
    themeId: z.string().optional()
  }),
  component: RouteComponent
});

function RouteComponent() {
  const { learningActivityDraftId } = Route.useParams();
  const search = Route.useSearch();
  const themeId = search.themeId;
  const withStaticContent = !!themeId;

  return (
    <LearningActivityStudioEditProviders
      learningActivityDraftId={learningActivityDraftId}
      initializeYjsDocumentStageData={initializeYjsDocumentStageData}
      withStaticContent={withStaticContent}
    >
      <LearningActivityStudioPage themeId={themeId} />
    </LearningActivityStudioEditProviders>
  );
}

const LearningActivityStudioPage = ({ themeId }: { themeId?: string }) => {
  const { t } = useTranslation('components');
  const { learningObjectDraftId: learningActivityDraftId } = useLearningObjectCommonData();
  const { isLoading, error } = useGetLearningActivityDraftByIdQuery(learningActivityDraftId);

  const setDeviceView = useStore(state => state.setDeviceView);

  const { height } = useViewportSize();

  useEffect(() => {
    setDeviceView('pc');
  }, [setDeviceView]);

  const { uiDirection } = useUiLanguage();

  if (error) {
    return (
      <Container h={height} size="100%">
        <Center h="100%">{t('studio.learningObjectDraftFetchError')}</Center>
      </Container>
    );
  }

  return (
    <ModalsProvider modals={modals}>
      <AppShell dir={uiDirection} padding="0" withBorder={false}>
        <AppShell.Header>
          <LearningActivityStudioTopBar />
        </AppShell.Header>
        <AppShell.Navbar>
          <LearningActivityToolbox
            widgetManifests={widgetManifests}
            pluginsContent={<Plugins category="embed" />}
            scoreWeight={<ScoreWeightSummary />}
            pages={Pages}
          />
        </AppShell.Navbar>
        <AppShell.Main mih="0">
          <Container
            style={{
              height: `calc(${height}px - ${rem(HEADER_HEIGHT_PX)})`,
              [stageContainerHeightVar]: `calc(${height}px - ${rem(HEADER_HEIGHT_PX)})`,
              marginTop: rem(HEADER_HEIGHT_PX),
              marginInlineStart: rem(sidebarWidth),
              padding: '0',
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '1fr min-content'
            }}
            size="100%"
          >
            <YjsSyncedWrapper>
              {!isLoading ? (
                <>
                  <RichTextToolbarTarget />
                  {!themeId && <LearningActivityPageWrapper />}
                  {themeId &&
                    (themeId === 'new' ? (
                      <CreateThemeContent
                        themeId={themeId}
                        currentContentPreviewComponent={<LearningActivityPageWrapper />}
                      />
                    ) : (
                      <UpdateThemeContent
                        themeId={themeId}
                        currentContentPreviewComponent={<LearningActivityPageWrapper />}
                      />
                    ))}
                </>
              ) : (
                <CustomLoadingOverlay visible />
              )}
            </YjsSyncedWrapper>
            <SideMenu />
          </Container>
        </AppShell.Main>
      </AppShell>
      <DndPreview />
    </ModalsProvider>
  );
};
